/** Utilities for calculating task duration rollups in hierarchical structures. */

const hasOpenChildren = (children = []) =>
  children.some((child) => !child.completed && child.status !== 'Backlog');

/**
 * Finds a task in the taskTree by its ID.
 *
 * @param {Object} task - The task object to search for.
 * @param {Array<Object>} taskTree - The hierarchical task tree.
 * @returns {Object|null} The matching tree node, or null.
 */
export const findTaskInTree = (task, taskTree) => {
  if (!taskTree || !task) return null;
  return taskTree.find((n) => n.id === task.id) || null;
};

/**
 * Derives duration and rollup status for a task based on its children in the tree.
 *
 * @param {Object} task - The task to check.
 * @param {Array<Object>} taskTree - The task tree.
 * @returns {{ displayDuration: number, isRollup: boolean }}
 */
export const getTaskRollupInfo = (task, taskTree) => {
  const node = findTaskInTree(task, taskTree);
  const children = node?.children ?? [];

  if (hasOpenChildren(children)) {
    return { displayDuration: node.duration ?? 0, isRollup: true };
  }
  return { displayDuration: task?.duration ?? 0, isRollup: false };
};

/**
 * Derives subtask progress (completed/total counts + percentage) for a task with children.
 * Returns null when the task has no children.
 *
 * @param {Object} task - The task object to check.
 * @param {Array<Object>} taskTree - The hierarchical task tree.
 * @param {number} remainingMinutes - rollup (uncompleted) duration, used as the denominator.
 * @returns {{ total: number, completed: number, percent: number } | null}
 */
export const computeSubtaskProgress = (task, taskTree, remainingMinutes) => {
  const node = findTaskInTree(task, taskTree);
  const children = node?.children ?? [];
  if (children.length === 0) return null;

  const total = children.length;
  const completed = children.filter((child) => child.completed).length;
  const doneMinutes = children.reduce(
    (sum, child) => sum + (child.completed ? child.duration || 0 : 0),
    0,
  );
  const totalMinutes = doneMinutes + (remainingMinutes || 0);
  const percent =
    totalMinutes > 0 ? Math.round((doneMinutes / totalMinutes) * 100) : 0;

  return { total, completed, percent };
};
